import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material';
import {
  RestoreFromTrash,
  DeleteForever,
  Search,
  Archive,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { observer } from 'mobx-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { PatientDto } from '../../../api-client';
import useStore from '../../../helpers/useStore';
import { PatientsContext } from '../../../stores/Patients/patients.provider';
import notificationStore from '../../../stores/Notification/notificationStore';

interface ArchivedPatient extends PatientDto {
  deletedAt?: Date;
}

interface ArchiveDialogState {
  open: boolean;
  type: 'restore' | 'delete' | null;
  patient: ArchivedPatient | null;
}

const PatientArchive: React.FC = observer(() => {
  const [archivedPatients, setArchivedPatients] = useState<ArchivedPatient[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogState, setDialogState] = useState<ArchiveDialogState>({
    open: false,
    type: null,
    patient: null,
  });
  const [confirmationText, setConfirmationText] = useState('');

  const patientStore = useStore(PatientsContext);

  // Fetch archived patients using store method
  const fetchArchivedPatients = async () => {
    setLoading(true);
    try {
      // Use the patientStore method instead of direct API call
      const data = await patientStore.getArchivedPatients(page + 1, rowsPerPage, searchTerm);
      setArchivedPatients(data.patients || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching archived patients:', error);
      notificationStore.showMessage('Fehler beim Laden der archivierten Patienten', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle restore patient using store method
  const handleRestorePatient = async (patient: ArchivedPatient) => {
    try {
      // Use the patientStore method instead of direct API call
      await patientStore.restorePatient(patient.id!);
      notificationStore.showMessage(`Patient ${patient.firstName} ${patient.lastName} wurde erfolgreich wiederhergestellt`, 'success');
      fetchArchivedPatients(); // Refresh the list
      setDialogState({ open: false, type: null, patient: null });
    } catch (error) {
      console.error('Error restoring patient:', error);
      // Error notification is already handled in the store method
    }
  };

  // Handle permanent delete using store method
  const handlePermanentDelete = async (patient: ArchivedPatient) => {
    if (confirmationText !== 'Löschen') {
      notificationStore.showMessage('Bitte geben Sie "Löschen" zur Bestätigung ein', 'warning');
      return;
    }

    try {
      // Use the patientStore method instead of direct API call
      await patientStore.permanentlyDeletePatient(patient.id!, confirmationText);
      notificationStore.showMessage(`Patient ${patient.firstName} ${patient.lastName} wurde endgültig gelöscht`, 'success');
      fetchArchivedPatients(); // Refresh the list
      setDialogState({ open: false, type: null, patient: null });
      setConfirmationText('');
    } catch (error: any) {
      console.error('Error permanently deleting patient:', error);
      const errorMessage = error?.response?.data?.message || 'Fehler beim endgültigen Löschen des Patienten';
      notificationStore.showMessage(errorMessage, 'error');
    }
  };

  // Helper function for safer date formatting
  const formatDate = (dateString?: string | Date | null, formatStr: string = 'dd.MM.yyyy') => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), formatStr, { locale: de });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Effects
  useEffect(() => {
    fetchArchivedPatients();
  }, [page, rowsPerPage, searchTerm]);

  // Initial data fetch
  useEffect(() => {
    fetchArchivedPatients();
  }, []);

  // Add debounce effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchArchivedPatients();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [page, rowsPerPage, searchTerm]);

  // Handle search with debounce
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open dialog
  const openDialog = (type: 'restore' | 'delete', patient: ArchivedPatient) => {
    setDialogState({ open: true, type, patient });
    setConfirmationText('');
  };

  // Close dialog
  const closeDialog = () => {
    setDialogState({ open: false, type: null, patient: null });
    setConfirmationText('');
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Archive sx={{ mr: 1, fontSize: 28, color: 'text.primary' }} />
          <Box>
            <Typography variant="h5">Patienten-Archiv</Typography>
            <Typography variant="body2" color="text.secondary">
              Hier finden Sie alle archivierten Patienten, die aus der Hauptliste entfernt wurden
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => {
              setSearchTerm('');
              setPage(0);
              setRowsPerPage(25);
              setTimeout(() => fetchArchivedPatients(), 100);
            }}
            startIcon={<Search />}
          >
            Alle anzeigen
          </Button>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Gesamtzahl: {totalCount} {totalCount === 1 ? 'Patient' : 'Patienten'}
          </Typography>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Nach Patienten suchen..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
          sx={{ maxWidth: '500px', flexGrow: 1 }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            fetchArchivedPatients();
          }}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          size="small"
        >
          {loading ? 'Wird geladen...' : 'Aktualisieren'}
        </Button>
      </Box>

      {/* Patients Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Geburtsdatum</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Versicherung</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Archiviert am</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary">
                        Archivierte Patienten werden geladen...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : archivedPatients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Archive sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5 }} />
                      <Typography color="text.secondary">
                        {searchTerm ? 'Keine Patienten für diese Suche gefunden' : 'Keine archivierten Patienten vorhanden'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                archivedPatients.map((patient) => (
                  <TableRow key={patient.id} hover>
                    <TableCell>
                      <Box>
                        <Tooltip title={`${patient.lastName}, ${patient.firstName}`} arrow>
                          <Typography variant="body2" fontWeight={500} sx={{ 
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {patient.lastName}, {patient.firstName}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          ID: {patient.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {formatDate(patient.dob)}
                    </TableCell>
                    <TableCell>
                      {patient.payer?.name || '-'}
                    </TableCell>
                    <TableCell>
                      {formatDate(patient.deletedAt, 'dd.MM.yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label="Archiviert" 
                        color="default" 
                        size="small"
                        icon={<Archive sx={{ fontSize: '0.8rem' }} />}
                        sx={{ borderRadius: '4px', backgroundColor: '#f5f5f5', color: '#333333' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Wiederherstellen">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openDialog('restore', patient)}
                          >
                            <RestoreFromTrash />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Endgültig löschen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDialog('delete', patient)}
                          >
                            <DeleteForever />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
        />
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogState.open}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: dialogState.type === 'delete' ? '#ffebee' : '#e3f2fd',
          color: dialogState.type === 'delete' ? '#d32f2f' : '#1976d2',
          display: 'flex', 
          alignItems: 'center',
          gap: 1
        }}>
          {dialogState.type === 'delete' ? (
            <>
              <DeleteForever /> Patient endgültig löschen
            </>
          ) : (
            <>
              <RestoreFromTrash /> Patient wiederherstellen
            </>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 2 }}>
          {dialogState.patient && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {dialogState.patient.lastName}, {dialogState.patient.firstName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Geburtsdatum: {formatDate(dialogState.patient.dob)}
                <br />
                Archiviert am: {formatDate(dialogState.patient.deletedAt, 'dd.MM.yyyy HH:mm')}
              </Typography>
              
              {dialogState.type === 'restore' ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Information</AlertTitle>
                  Wenn Sie diesen Patienten wiederherstellen, wird er wieder in der normalen Patientenliste angezeigt und kann wie gewohnt bearbeitet werden.
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>Achtung - Nicht rückgängig machbar!</AlertTitle>
                    Diese Aktion <strong>kann nicht rückgängig gemacht werden</strong>! 
                    Der Patient wird unwiderruflich gelöscht.
                    <br /><br />
                    Hinweis: Wenn der Patient noch mit Terminen oder Rezepten verknüpft ist, kann die Löschung nicht durchgeführt werden.
                  </Alert>
                  <TextField
                    fullWidth
                    label="Zur Bestätigung 'Löschen' eingeben"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    placeholder="Löschen"
                    error={confirmationText !== '' && confirmationText !== 'Löschen'}
                    helperText={confirmationText !== '' && confirmationText !== 'Löschen' ? 'Bitte genau "Löschen" eingeben' : ''}
                    sx={{ mb: 2 }}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={closeDialog} 
            variant="outlined"
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color={dialogState.type === 'restore' ? 'primary' : 'error'}
            onClick={() => {
              if (dialogState.type === 'restore') {
                handleRestorePatient(dialogState.patient!);
              } else {
                handlePermanentDelete(dialogState.patient!);
              }
            }}
            disabled={dialogState.type === 'delete' && confirmationText !== 'Löschen'}
            startIcon={dialogState.type === 'restore' ? <RestoreFromTrash /> : <DeleteForever />}
          >
            {dialogState.type === 'restore' ? 'Wiederherstellen' : 'Endgültig löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default PatientArchive;
